// app/javascript/controllers/donation_tracking_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "favoriteCharity",
    "amount",
    "split",
    "inviteCode"
  ]

  initialize() {
    this.dataLayer = window.dataLayer = window.dataLayer || []

    // Initialize debounced methods
    this.debouncedAmountTracking = this.debounce(this.trackAmount.bind(this), 1000)
    this.debouncedSplitTracking = this.debounce(this.trackSplit.bind(this), 1000)
    this.debouncedInviteCodeTracking = this.debounce(this.trackInviteCode.bind(this), 1000)
    this.debouncedCharitySelectedTracking = this.debounce(this.trackCharitySelected.bind(this), 1000)
  }

  // Vanilla JS debounce function
  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  highCharityName() {
    var e = document.querySelector('input[name="donation[top_charity_id]"]:checked') ||
            document.getElementById('donation_top_charity_name')
    if (e) {
      return e.dataset['name'];
    } else {
      return null;
    }
  }

  // Step 1: Track charity selection
  charitySelected(event) {
    const charityType = event.target.dataset.charityType
    if(charityType === 'effective' && !event.target.checked) return
    this.debouncedCharitySelectedTracking(charityType, event.target)
  }

  trackCharitySelected(charityType, target) {
    const item = charityType === 'favorite' ? this.favoriteItem() : this.effectiveItem();

    this.dataLayer.push({
      event: 'donation_charity_selected',
      donation_item_type: charityType,
      donation_items: [item]
    })
  }

  // Step 2: Track donation amount
  amountEntered(event) {
    this.debouncedAmountTracking(event.target.value)
  }

  // Separate tracking method for amounts
  trackAmount(value) {
    const amount = parseFloat(value)

    if (!isNaN(amount)) {
      this.dataLayer.push({
        event: 'donation_amount_set',
        donation_value: amount,
        donation_currency: 'USD'
      })
    }
  }

  // Step 3: Track split configuration
  splitConfigured(event) {
    this.debouncedSplitTracking(event.target.value)
  }

  // Separate tracking method for split
  trackSplit(split) {
    if(!this.amountTarget.value) return

    this.dataLayer.push({
      event: 'donation_split_set',
      spit_ratio: this.splitTarget.value,
      donation_items: this.items()
    })
  }

  // Step 4: Track invite code
  inviteCodeEntered(event) {
    this.debouncedInviteCodeTracking(event.target.value)
  }

  trackInviteCode(code){
    this.dataLayer.push({
      event: 'donation_invite_code_entered',
      invite_code: code
    })
  }

  favoriteItem() {
    const split = this.splitTarget.value || 0.5
    const totalAmount = parseFloat(this.amountTarget.value) || 0
    const favoritePercentage = parseInt((1-split) * 100)

    if (this.favoriteCharityTarget.value) {
      return {
        item_name: this.favoriteCharityTarget.value,
        price: totalAmount * favoritePercentage / 100,
        quantity: 1,
        item_category: 'Charity',
        item_category2: 'Favorite'
      }
    }
  }

  effectiveItem() {
    const split = this.splitTarget.value || 0.5
    const totalAmount = parseFloat(this.amountTarget.value) || 0
    const effectivePercentage = parseInt(split * 100)

    if (this.favoriteCharityTarget.value) {
      return {
        item_name: this.highCharityName(),
        price: totalAmount * effectivePercentage / 100,
        quantity: 1,
        item_category: 'Charity',
        item_category2: 'Effective'
      }
    }
  }

  items() {
    return [this.favoriteItem(), this.effectiveItem()].filter(n => n);
  }

  // Step 5: Track checkout initiation
  beginCheckout(event) {
    this.dataLayer.push({
      event: 'begin_checkout',
      donation_currency: 'USD',
      donation_value: parseFloat(this.amountTarget.value) || 0,
      invite_code: this.inviteCodeTarget.value || '',
      spit_ratio: this.splitTarget.value,
      donation_items: this.items()
    })
  }

  // Step 6: Track payment initiation
  paymentInitiated(event) {
    this.dataLayer.push({
      event: 'add_payment_info',
      donation_currency: 'USD',
      donation_value: parseFloat(this.amountTarget.value) || 0,
      invite_code: this.inviteCodeTarget.value || '',
      spit_ratio: this.splitTarget.value,
      donation_items: this.items()
    })
  }

  // Error tracking
  trackError(errorType, errorMessage, step) {
    this.dataLayer.push({
      event: 'donation_error',
      error_type: errorType,
      error_message: errorMessage,
      donation_step: step
    })
  }
}
